import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { ScheduleModal } from "@/components/ScheduleModal";
import { ProgressTracker } from "@/components/ProgressTracker";
import { ChatPanel } from "@/components/ChatPanel";
import { ReviewForm } from "@/components/ReviewForm";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { format, parseISO } from "date-fns";
import { PhoneCall, FolderGit2, MessageSquare, Loader2, CheckCircle2, PlusCircle, CreditCard } from "lucide-react";

export default function ClientDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [active, setActive] = useState(null);
  const [modal, setModal] = useState(false);

  const load = async () => {
    const { data } = await api.get("/my/projects");
    setProjects(data);
    setActive((cur) => data.find((p) => p.id === cur?.id) || data[0] || null);
  };

  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); /* eslint-disable-next-line */ }, []);

  if (projects === null) {
    return (
      <div className="grain min-h-screen bg-background">
        <Header onSchedule={() => setModal(true)} />
        <div className="flex h-[60vh] items-center justify-center text-zinc-500">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="grain min-h-screen bg-background text-white">
      <Header onSchedule={() => setModal(true)} />
      <div className="mx-auto max-w-7xl px-6 py-8">
        <div className="mb-6">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">// Welcome back</p>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">{user?.name}</h1>
        </div>

        {projects.length === 0 ? (
          <div className="border border-white/10 bg-[#0A0A0B] p-10 text-center">
            <FolderGit2 className="mx-auto h-10 w-10 text-primary" />
            <h2 className="mt-4 font-display text-xl font-semibold">No projects yet</h2>
            <p className="mt-2 text-sm text-zinc-400">Request a call back to kick off your first build.</p>
            <Button data-testid="empty-schedule-btn" onClick={() => setModal(true)}
              className="mt-5 rounded-full bg-primary text-white hover:bg-primary/90">
              <PhoneCall className="mr-2 h-4 w-4" /> Request a Call Back
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
            {/* Project list */}
            <div className="space-y-2 lg:col-span-3">
              {projects.map((p) => (
                <button key={p.id} data-testid={`project-item-${p.id}`} onClick={() => setActive(p)}
                  className={`w-full border p-4 text-left transition-colors ${
                    active?.id === p.id ? "border-primary bg-primary/10" : "border-white/10 bg-[#0A0A0B] hover:border-white/25"
                  }`}>
                  <p className="truncate text-sm font-medium">{p.business_name || p.what_needs_made?.slice(0, 24) || "Project"}</p>
                  <p className="mt-1 font-mono text-[11px] text-zinc-500">{format(parseISO(p.created_at), "MMM d, yyyy")}</p>
                  <Badge className="mt-2 rounded-sm bg-white/5 text-[10px] font-mono uppercase text-primary">{p.status}</Badge>
                </button>
              ))}
              <Button data-testid="new-meeting-btn" variant="ghost" onClick={() => setModal(true)}
                className="w-full rounded-none border border-dashed border-white/15 text-zinc-400 hover:text-white">
                <PlusCircle className="mr-2 h-4 w-4" /> New request
              </Button>
            </div>

            {/* Detail */}
            {active && (
              <div className="space-y-4 lg:col-span-9">
                {/* Progress */}
                <div className="border border-white/10 bg-[#0A0A0B] p-6">
                  <div className="mb-6 flex items-center justify-between">
                    <h2 className="font-display text-lg font-semibold">Build Progress</h2>
                    {active.status === "Done" && (
                      <Badge className="rounded-sm bg-emerald-500/15 text-emerald-400">
                        <CheckCircle2 className="mr-1 h-3 w-3" /> Completed
                      </Badge>
                    )}
                  </div>
                  <ProgressTracker status={active.status} />
                  {active.status === "Done" && (
                    <div className="mt-6 border-t border-white/10 pt-5">
                      <p className="text-sm text-zinc-400">Project complete — need more work done?</p>
                      <Button data-testid="followup-schedule-btn" onClick={() => setModal(true)}
                        className="mt-3 rounded-full bg-primary text-white hover:bg-primary/90">
                        <PhoneCall className="mr-2 h-4 w-4" /> Request another call back
                      </Button>
                      <div><ReviewForm /></div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {/* Callback */}
                  <div className="border border-white/10 bg-[#0A0A0B] p-6">
                    <div className="flex items-center gap-2 text-primary"><PhoneCall className="h-5 w-5" />
                      <h3 className="font-display font-semibold text-white">Your Callback</h3></div>
                    <p className="mt-3 font-mono text-xs text-zinc-500">CALL BACK REQUESTED</p>
                    <p className="text-sm text-zinc-200">We'll call you at {active.phone || "your number"}.</p>
                    {active.callback_at && (
                      <p className="mt-2 text-sm text-zinc-300">
                        Preferred date: <span className="text-white">{format(parseISO(active.callback_at), "EEE, MMM d, yyyy")}</span>
                      </p>
                    )}
                    {active.callback_note && (
                      <p className="mt-1 text-sm text-zinc-400">{active.callback_note}</p>
                    )}
                    <p className="mt-4 text-sm text-zinc-500">Our team will call you around your preferred time. Need to change something? Message us below.</p>
                  </div>

                  {/* Project brief */}
                  <div className="border border-white/10 bg-[#0A0A0B] p-6">
                    <h3 className="font-display font-semibold">Project Brief</h3>
                    <p className="mt-3 whitespace-pre-wrap text-sm text-zinc-300">{active.what_needs_made}</p>
                    {active.domain_name && <p className="mt-3 font-mono text-xs text-zinc-500">DOMAIN: {active.domain_name}</p>}
                  </div>
                </div>

                {/* Payments */}
                <div className="border border-white/10 bg-[#0A0A0B] p-6" data-testid="client-payments">
                  <div className="mb-4 flex items-center gap-2 text-primary"><CreditCard className="h-5 w-5" />
                    <h3 className="font-display font-semibold text-white">Payments</h3></div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {[
                      { key: "deposit", label: "Deposit", amount: active.deposit_amount, paid: active.deposit_paid },
                      { key: "final", label: "Final payment", amount: active.final_amount, paid: active.final_paid },
                    ].map((row) => (
                      <div key={row.key} data-testid={`client-pay-${row.key}`} className="border border-white/10 bg-black p-4">
                        <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">{row.label}</p>
                        <div className="mt-1 flex items-center justify-between">
                          <p className="font-display text-lg font-semibold">{row.amount ? `$${Number(row.amount).toFixed(2)}` : "—"}</p>
                          {row.paid
                            ? <Badge className="rounded-sm bg-emerald-500/15 text-[10px] text-emerald-400"><CheckCircle2 className="mr-1 h-3 w-3" /> Paid</Badge>
                            : row.amount
                              ? <Badge className="rounded-sm bg-amber-500/15 text-[10px] text-amber-400">Due</Badge>
                              : <Badge className="rounded-sm bg-white/5 text-[10px] text-zinc-400">Not set</Badge>}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-zinc-500">Payment instructions are shared directly by our team. Amounts update here once set.</p>
                </div>

                {/* Chat */}
                <div className="border border-white/10 bg-[#0A0A0B] p-6">
                  <div className="mb-4 flex items-center gap-2 text-primary"><MessageSquare className="h-5 w-5" />
                    <h3 className="font-display font-semibold text-white">Direct Message</h3></div>
                  <ChatPanel projectId={active.id} meRole="client" />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <ScheduleModal open={modal} onOpenChange={(v) => { setModal(v); if (!v) load(); }} />
    </div>
  );
}
